import { storeWrapper } from '../storeWrapper'
import { debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { extractClientMatterNumbers } from '@repo/utils/clientMatterNumbers'
import { useUrlStore } from '@repo/store/useUrlStore'
import { useToastStore } from '@repo/store/useToastStore'
const { navigateWithClientMatterNo } = useUrlStore.getState()

const initialState = {
  cardData: [
    {
      cardId: null,
      cardEnabled: true,
      cardOpen: false
    }
  ],
  cardExpanded: 'matter',
  emailSendingCurrentMatterData: {
    clientMatterNo: null,
    clientNo: null,
    dateArchived: null,
    emailClientReferenceId: null,
    feeEarnerDescription: null,
    forename: null,
    highRisk: false,
    isArchived: false,
    isUserRestricted: false,
    matterDescription: null,
    matterNo: null,
    showMatterPill: false,
    surname: null,
    workTypeId: null
  },
  emailTemplates: null,
  selectedEmailTemplatePosition: 'append',
  selectedEmailTemplate: null,
  mergedEmailTemplate: null,
  recipientType: {
    type: null,
    service: null,
    endpoint: null
  },
  toRecipients: {
    clients: [],
    contacts: [],
    organisations: []
  },
  ccRecipients: {
    clients: [],
    contacts: [],
    organisations: []
  },
  bccRecipients: {
    clients: [],
    contacts: [],
    organisations: []
  },
  emailModalOpen: false,
  matterHistoryContentId: null,
  matterHistoryPackageId: null,
  allMatterHistoryFolderIds: [],
  attachments: [],
  packages: [],
  attachedAttachments: [],
  attachedPackages: [],
  emailPayload: null,
  matterHistorySettings: {
    deleteDuplicateImagesAboveCount: null,
    emailConversationId: null,
    ignoreInlineImagesBelowKilobytes: null,
    isIgnoreInlineImages: false,
    isSaveEmailAttachments: false,
    isSaveEmailAttachmentsSeparately: false,
    isSaveEmailBody: false,
    matterHistoryDetailForReceivedEmail: null,
    matterHistoryDetailForSentEmail: null,
    matterHistoryEmailConfigurationId: null,
    matterHistoryFolderId: null,
    matterHistoryTypeId: null,
    retentionDateType: null,
    retentionPolicyId: null
  },
  matterHistorySettingsDefaults: {
    deleteDuplicateImagesAboveCount: null,
    emailConversationId: null,
    ignoreInlineImagesBelowKilobytes: null,
    isIgnoreInlineImages: false,
    isSaveEmailAttachments: false,
    isSaveEmailAttachmentsSeparately: false,
    isSaveEmailBody: false,
    matterHistoryDetailForReceivedEmail: null,
    matterHistoryDetailForSentEmail: null,
    matterHistoryEmailConfigurationId: null,
    matterHistoryFolderId: null,
    matterHistoryTypeId: null,
    retentionDateType: null,
    retentionPolicyId: null
  },
  emailDetailsFromOsprey1: null,
  addAttachmentCallBack: null,
  addPackageCallBack: null,
  mostRecentlySelectedAttachment: null,
  mostRecentlySelectedPackage: null,
  emailType: 'new'
}

export const useEmailCardStore = storeWrapper('useEmailCardStore', (set, get) => ({
  ...initialState,
  handleCardToggle: (cardId) => {
    if (cardId == null) return

    const { cardData, cardExpanded } = get()
    const enabled = cardData.some((item) => item.cardId === cardId && item.cardEnabled)

    if (enabled) {
      if (cardExpanded !== cardId) {
        set({ cardExpanded: cardId })
      } else {
        set({ cardExpanded: null })
      }
    }
  },
  fetchEmailTemplates: async ({ name, workTypeId, expertTypeId, startAtIndex, pageSize }) => {
    return new Promise((resolve) => {
      debounce({
        id: 'fetchEmailTemplates',
        delay: 300,
        callback: async () => {
          let emailTemplates = await ospreyAPI({
            service: 'lookups',
            endpoint: 'EmailTemplate/GetAllBySearchCriteria',
            data: {
              name: name ?? null,
              workTypeId: workTypeId ?? null,
              expertTypeId: expertTypeId ?? null,
              startAtIndex: startAtIndex ?? 0,
              pageSize: pageSize ?? 25
            }
          })

          set({ emailTemplates: emailTemplates })
          resolve(emailTemplates)
        }
      })
    })
  },
  fetchSingleEmailTemplate: async ({ emailTemplateId, clientNo, matterNo }) => {
    let emailTemplate = await ospreyAPI({
      service: 'emails',
      endpoint: `EmailTemplate/GetMergedEmailTemplate`,
      data: {
        emailTemplateId: emailTemplateId,
        clientNo: clientNo ?? null,
        matterNo: matterNo ?? null
      }
    })

    set({ mergedEmailTemplate: emailTemplate })
  },
  fetchRecipients: async ({ recipientType, emailAddress, typeId, searchTerm, startAtIndex = 0, pageSize = 25 }) => {
    const { emailSendingCurrentMatterData } = get()

    if (!emailSendingCurrentMatterData?.clientMatterNo) return

    const { clientNo, matterNo } = extractClientMatterNumbers({
      clientMatterNo: emailSendingCurrentMatterData.clientMatterNo
    })

    // Determine metadata for the API call
    const meta = {
      type: recipientType,
      service: recipientType === 'clients' ? 'matters' : 'lookups',
      endpoint:
        recipientType === 'clients'
          ? '/Matter/GetAllAssociatedNames'
          : recipientType === 'contacts'
            ? '/Contact/GetAllBySearchTerm'
            : '/Organisation/GetAllBySearchTerm'
    }

    // Save metadata (only) in the store
    set({
      recipientType: meta
    })

    // Prepare request data
    const requestData =
      recipientType === 'clients'
        ? {
            EmailAddress: emailAddress ?? null,
            StartAtIndex: startAtIndex,
            PageSize: pageSize,
            ClientNo: clientNo ?? null,
            MatterNo: matterNo ?? null
          }
        : {
            SearchTerm: searchTerm ?? null,
            ExpertTypeId: typeId ?? null,
            StartAtIndex: startAtIndex,
            PageSize: pageSize,
            HasEmail: true,
            OrderByColumns: [
              {
                columnName: recipientType === 'organisations' ? 'name' : 'surname',
                direction: 0
              }
            ]
          }

    // Return promise that resolves after debounce
    return new Promise((resolve, reject) => {
      debounce({
        id: `fetchRecipients-${recipientType}`,
        delay: 300,
        callback: async () => {
          try {
            const recipients = await ospreyAPI({
              service: meta.service,
              endpoint: meta.endpoint,
              data: requestData
            })
            resolve(recipients)
          } catch (error) {
            reject(error)
          }
        }
      })
    })
  },
  fetchAttachments: async ({
    clientMatterNo,
    startAtIndex,
    searchTerm,
    extensions,
    matterHistoryFolderId,
    pageSize,
    parentMatterHistoryId = null,
    shouldSaveToStore = true
  }) => {
    if (clientMatterNo == null) return

    const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })

    if (clientNo == null || matterNo == null) return

    return new Promise((resolve) => {
      debounce({
        id: `fetchAttachments-${clientMatterNo}`,
        delay: 300,
        callback: async () => {
          try {
            const attachments = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `/MatterHistoryContent/GetAllBySearchTerms`,
              data: {
                ClientNo: clientNo,
                MatterNo: matterNo,
                Description: searchTerm ?? null,
                StartAtIndex: startAtIndex ?? 0,
                PageSize: pageSize ?? 25,
                Extension: extensions ?? null,
                MatterHistoryFolderId: matterHistoryFolderId ?? null,
                ParentMatterHistoryId: parentMatterHistoryId ?? null
              }
            })
            if (shouldSaveToStore) set({ attachments })
            resolve(attachments)
          } catch (error) {
            console.error('Error fetching attachments:', error)
            throw error
          }
        }
      })
    })
  },
  fetchPackages: async ({ clientMatterNo }) => {
    if (clientMatterNo == null) return

    const { clientNo, matterNo } = extractClientMatterNumbers({ clientMatterNo })

    if (clientNo == null || matterNo == null) return

    return new Promise((resolve) => {
      debounce({
        id: `fetchPackages-${clientMatterNo}`,
        delay: 300,
        callback: async () => {
          try {
            let packages = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `/MatterHistoryPackage/GetAllByClientNoAndMatterNo`,
              data: {
                clientNo: clientNo,
                matterNo: matterNo
              }
            })

            packages = packages.map((packageItem) => {
              const { clientNo, matterNo, lastModifiedDate, fileExtension } = packageItem
              const date = lastModifiedDate.split('T')[0].replace(/-/g, '')
              return {
                ...packageItem,
                filename: `Package_${clientNo}-${matterNo}_${date}.${fileExtension}`
              }
            })

            set({ packages })
            resolve(packages)
          } catch (error) {
            console.error('Error fetching packages:', error)
            throw error
          }
        }
      })
    })
  },
  fetchAttachmentContents: async ({ matterHistoryContentId, isConvertWordToPdf = false, filename, fileSize }) => {
    if (!matterHistoryContentId) return

    return new Promise((resolve) => {
      debounce({
        id: `fetchAttachmentContents-${matterHistoryContentId}`,
        delay: 300,
        callback: async () => {
          const isVeryLargeFile = fileSize && fileSize > 5000 // larger than 5MB
          const isLargeFileAndHasToBeConvertedToPdf = fileSize && fileSize > 2000 && isConvertWordToPdf // larger than 2MB and has to be converted to PDF. This is slower, so we show a warning toast.
          const shouldShowToasts = isVeryLargeFile || isLargeFileAndHasToBeConvertedToPdf

          try {
            if (shouldShowToasts) {
              useToastStore.getState().showToast({
                type: 'warning',
                title: `Downloading attachment`,
                text: `'${filename}' is a large file. Please wait while it is downloaded from Matter History`
              })
            }

            const file = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `/MatterHistoryContent/GetContentAsFile`,
              data: {
                matterHistoryContentId,
                isConvertWordToPdf
              },
              timeout: 120000,
              responseType: 'blob',
              headers: { Accept: '*/*', 'Content-Type': 'application/json' },
              successToast: shouldShowToasts ? {
                type: 'success',
                title: `Download complete`,
                text: filename
              } : null
            })

            resolve(file)
          } catch (error) {
            console.error('Error fetching attachment contents:', error)
            throw error
          }
        }
      })
    })
  },
  fetchPackageContents: async ({ matterHistoryPackageId }) => {
    if (!matterHistoryPackageId) return

    return new Promise((resolve) => {
      debounce({
        id: `fetchPackageContents-${matterHistoryPackageId}`,
        delay: 300,
        callback: async () => {
          try {
            const file = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `/MatterHistoryPackage/GetContentAsFile`,
              data: {
                matterHistoryPackageId
              },
              responseType: 'blob',
              headers: { Accept: '*/*', 'Content-Type': 'application/json' }
            })

            resolve(file)
          } catch (error) {
            console.error('Error fetching package contents:', error)
            throw error
          }
        }
      })
    })
  },
  fetchAllMatterHistoryFolderIds: async () => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchAllMatterHistoryFolderIds`,
        delay: 300,
        callback: async () => {
          try {
            const allMatterHistoryFolderIds = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `/MatterHistoryFolder/GetAll`
            })
            set({ allMatterHistoryFolderIds })
            resolve(allMatterHistoryFolderIds)
          } catch (error) {
            console.error('Error fetching allMatterHistoryFolderIds:', error)
            throw error
          }
        }
      })
    })
  },
  setCardId: (cardId) => {
    if (cardId == null) return

    const { cardData } = get()
    // Filter out any placeholder entries (cardId null)
    const validCards = Array.isArray(cardData) ? cardData.filter((item) => item.cardId != null) : []
    const exists = validCards.some((item) => item.cardId === cardId)

    if (!exists) {
      set({
        // Remove initial null entry by using only validCards
        cardData: [
          ...validCards,
          {
            cardId,
            cardEnabled: true,
            cardOpen: false
          }
        ]
      })
    }
  },
  setCurrentCardEnabled: ({ cardId }) => {
    if (cardId == null) return

    const { cardData } = get()
    set({
      cardData: Array.isArray(cardData)
        ? cardData.map((item) => (item.cardId === cardId ? { ...item, cardEnabled: !item.cardEnabled } : item))
        : cardData
    })
  },
  setEmailSendingCurrentMatterData: ({
    clientMatterNo,
    clientNo,
    emailClientReferenceId,
    matterNo,
    matterDescription,
    dateArchived,
    feeEarnerDescription,
    forename,
    highRisk = false,
    isArchived = false,
    isUserRestricted = false,
    showMatterPill = false,
    surname,
    workTypeId
  }) => {
    set({
      emailSendingCurrentMatterData: {
        clientMatterNo: clientMatterNo ?? null,
        clientNo: clientNo ?? null,
        emailClientReferenceId: emailClientReferenceId ?? null,
        matterNo: matterNo ?? null,
        matterDescription: matterDescription ?? null,
        dateArchived: dateArchived ?? null,
        feeEarnerDescription: feeEarnerDescription ?? null,
        forename: forename ?? null,
        highRisk: highRisk ?? null,
        isArchived: isArchived ?? null,
        isUserRestricted: isUserRestricted ?? null,
        showMatterPill: showMatterPill ?? null,
        surname: surname ?? null,
        workTypeId: workTypeId ?? null
      }
    })
  },
  setToRecipients: ({ email, name, recipientType }) => {
    set((state) => {
      const { toRecipients } = state
      const updatedRecipients = { ...toRecipients }

      if (recipientType === 'clients') {
        updatedRecipients.clients = [
          ...(updatedRecipients.clients || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      } else if (recipientType === 'contacts') {
        updatedRecipients.contacts = [
          ...(updatedRecipients.contacts || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      } else if (recipientType === 'organisations') {
        updatedRecipients.organisations = [
          ...(updatedRecipients.organisations || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      }

      return { toRecipients: updatedRecipients }
    })
  },
  removeToRecipient: ({ email }) => {
    const { toRecipients } = get()
    const updatedRecipients = { ...toRecipients }

    updatedRecipients.clients = updatedRecipients.clients.filter((recipient) => recipient.email !== email)
    updatedRecipients.contacts = updatedRecipients.contacts.filter((recipient) => recipient.email !== email)
    updatedRecipients.organisations = updatedRecipients.organisations.filter((recipient) => recipient.email !== email)

    set({ toRecipients: updatedRecipients })
  },
  setCcRecipients: ({ email, name, recipientType }) => {
    set((state) => {
      const { ccRecipients } = state
      const updatedRecipients = { ...ccRecipients }

      if (recipientType === 'clients') {
        updatedRecipients.clients = [
          ...(updatedRecipients.clients || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      } else if (recipientType === 'contacts') {
        updatedRecipients.contacts = [
          ...(updatedRecipients.contacts || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      } else if (recipientType === 'organisations') {
        updatedRecipients.organisations = [
          ...(updatedRecipients.organisations || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      }

      return { ccRecipients: updatedRecipients }
    })
  },
  removeCcRecipient: ({ email }) => {
    const { ccRecipients } = get()
    const updatedRecipients = { ...ccRecipients }

    updatedRecipients.clients = updatedRecipients.clients.filter((recipient) => recipient.email !== email)
    updatedRecipients.contacts = updatedRecipients.contacts.filter((recipient) => recipient.email !== email)
    updatedRecipients.organisations = updatedRecipients.organisations.filter((recipient) => recipient.email !== email)

    set({ ccRecipients: updatedRecipients })
  },
  setBccRecipients: ({ email, name, recipientType }) => {
    set((state) => {
      const { bccRecipients } = state
      const updatedRecipients = { ...bccRecipients }

      if (recipientType === 'clients') {
        updatedRecipients.clients = [
          ...(updatedRecipients.clients || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      } else if (recipientType === 'contacts') {
        updatedRecipients.contacts = [
          ...(updatedRecipients.contacts || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      } else if (recipientType === 'organisations') {
        updatedRecipients.organisations = [
          ...(updatedRecipients.organisations || []),
          {
            name: name ?? null,
            email: email
          }
        ]
      }

      return { bccRecipients: updatedRecipients }
    })
  },
  removeBccRecipient: ({ email }) => {
    const { bccRecipients } = get()
    const updatedRecipients = { ...bccRecipients }

    updatedRecipients.clients = updatedRecipients.clients.filter((recipient) => recipient.email !== email)
    updatedRecipients.contacts = updatedRecipients.contacts.filter((recipient) => recipient.email !== email)
    updatedRecipients.organisations = updatedRecipients.organisations.filter((recipient) => recipient.email !== email)

    set({ bccRecipients: updatedRecipients })
  },
  setSelectedEmailTemplate: (emailTemplate) => {
    set({
      selectedEmailTemplate: emailTemplate
    })
  },
  setSelectedEmailTemplatePosition: (position) => {
    set({
      selectedEmailTemplatePosition: position
    })
  },
  resetEmailSendingCurrentMatterData: () => {
    set({
      emailSendingCurrentMatterData: {
        clientMatterNo: null,
        clientNo: null,
        dateArchived: null,
        emailClientReferenceId: null,
        feeEarnerDescription: null,
        forename: null,
        highRisk: false,
        isArchived: false,
        isUserRestricted: false,
        matterDescription: null,
        matterNo: null,
        showMatterPill: false,
        surname: null,
        workTypeId: null
      },
      selectedEmailTemplatePosition: 'append',
      selectedEmailTemplate: null,
      mergedEmailTemplate: null,
      addAttachmentCallBack: null,
      addPackageCallBack: null
    })
  },
  setEmailModalOpen: ({ isOpen }) => {
    set({
      emailModalOpen: isOpen
    })
  },
  setMatterHistoryContentId: ({ matterHistoryContentId }) => {
    set({
      matterHistoryContentId: matterHistoryContentId ?? null
    })
  },
  setMatterHistoryPackageId: ({ matterHistoryPackageId }) => {
    set({
      matterHistoryPackageId: matterHistoryPackageId ?? null
    })
  },
  toggleSetAttachedAttachments: ({ attachment, matterHistoryContentIsConvertWordToPdfs }) => {
    if (!attachment.matterHistoryContentId) return

    const { attachedAttachments, addAttachmentCallBack } = get()
    const convertToPdf = matterHistoryContentIsConvertWordToPdfs ?? false

    // Find by both matterHistoryContentId AND matterHistoryContentIsConvertWordToPdfs
    // This treats the same file with different convert settings as separate attachments
    const existingIndex = attachedAttachments.findIndex(
      (att) => att.matterHistoryContentId === attachment.matterHistoryContentId
      // && att.matterHistoryContentIsConvertWordToPdfs === convertToPdf
    )
    const exists = existingIndex !== -1

    // Modify filename if converting to PDF
    let modifiedFilename = attachment.filename
    if (convertToPdf && attachment.filename) {
      modifiedFilename = attachment.filename.replace(/\.docx?$/i, '.pdf')
    }

    const mostRecentlySelectedAttachment = { attachment, matterHistoryContentIsConvertWordToPdfs }

    if (exists) {
      // Same attachment with same convert setting - toggle off (remove)
      const updatedAttachments = attachedAttachments.filter(
        (att) =>
          !(
            (att.matterHistoryContentId === attachment.matterHistoryContentId)
            // && att.matterHistoryContentIsConvertWordToPdfs === convertToPdf
          )
      )
      set({
        attachedAttachments: updatedAttachments,
        mostRecentlySelectedAttachment
      })
    } else {
      // Doesn't exist with this convert setting - add it
      set((state) => ({
        attachedAttachments: [
          ...state.attachedAttachments,
          {
            ...attachment,
            filename: modifiedFilename,
            matterHistoryContentIsConvertWordToPdfs: convertToPdf
          }
        ],
        mostRecentlySelectedAttachment
      }))
    }

    // this is used by the Outlook addin to be notified when an attachment is clicked
    if (addAttachmentCallBack && typeof addAttachmentCallBack === 'function') addAttachmentCallBack(attachment)
  },
  toggleSetAttachedPackages: ({ packageItem }) => {
    if (!packageItem.matterHistoryPackageId) return

    const { attachedPackages, addPackageCallBack } = get()
    const exists = attachedPackages.some((pkg) => pkg.matterHistoryPackageId === packageItem.matterHistoryPackageId)

    if (exists) {
      const updatedPackages = attachedPackages.filter(
        (pkg) => pkg.matterHistoryPackageId !== packageItem.matterHistoryPackageId
      )
      set({ attachedPackages: updatedPackages, mostRecentlySelectedPackage: packageItem })
    } else {
      set((state) => ({
        attachedPackages: [...state.attachedPackages, packageItem],
        mostRecentlySelectedPackage: packageItem
      }))
    }

    // this is used by the Outlook addin to be notified when a package is clicked
    if (addPackageCallBack && typeof addPackageCallBack === 'function') addPackageCallBack(packageItem)
  },
  removeAttachedAttachment: ({ matterHistoryContentId }) => {
    const { attachedAttachments } = get()
    const updatedAttachments = attachedAttachments.filter(
      (att) => att.matterHistoryContentId !== matterHistoryContentId
    )
    set({ attachedAttachments: updatedAttachments })
  },
  removeAttachedPackage: ({ matterHistoryPackageId }) => {
    const { attachedPackages } = get()
    const updatedPackages = attachedPackages.filter((pkg) => pkg.matterHistoryPackageId !== matterHistoryPackageId)
    set({ attachedPackages: updatedPackages })
  },
  clearAttachmentsAndPackages: () => {
    set({ attachedAttachments: [], attachedPackages: [] })
  },
  getPayloadForEmailTemplate: ({ emailAddress, subject, body, templateData, templateName }) => {
    const { toRecipients, ccRecipients, bccRecipients } = get()

    const allToRecipients = [
      ...(toRecipients.clients || []).map(({ name, email }) => ({ displayName: name, address: email })),
      ...(toRecipients.contacts || []).map(({ name, email }) => ({ displayName: name, address: email })),
      ...(toRecipients.organisations || []).map(({ name, email }) => ({ displayName: name, address: email }))
    ]

    const allCcRecipients = [
      ...(ccRecipients.clients || []).map(({ name, email }) => ({ displayName: name, address: email })),
      ...(ccRecipients.contacts || []).map(({ name, email }) => ({ displayName: name, address: email })),
      ...(ccRecipients.organisations || []).map(({ name, email }) => ({ displayName: name, address: email }))
    ]

    const allBccRecipients = [
      ...(bccRecipients.clients || []).map(({ name, email }) => ({ displayName: name, address: email })),
      ...(bccRecipients.contacts || []).map(({ name, email }) => ({ displayName: name, address: email })),
      ...(bccRecipients.organisations || []).map(({ name, email }) => ({ displayName: name, address: email }))
    ]

    const emailPayload = {
      subject: subject,
      body: body,
      isBodyHtml: true,
      from: {
        address: emailAddress,
        displayName: null
      },
      to: allToRecipients,
      cc: allCcRecipients,
      bcc: allBccRecipients,
      templateData: templateData ?? null,
      templateName: templateName ?? null
    }

    set({ emailPayload: emailPayload })
  },
  setMatterHistorySettings: (updates) => {
    const newSettings = updates.matterHistorySettings ?? updates

    set({
      matterHistorySettings: {
        ...get().matterHistorySettings,
        ...newSettings
      }
    })
  },
  setMatterHistorySettingsDefaults: (updates) => {
    const newSettings = updates.matterHistorySettingsDefaults ?? updates
    set({
      matterHistorySettingsDefaults: {
        ...get().matterHistorySettingsDefaults,
        ...newSettings
      }
    })
  },
  sendEmailWithAttachments: async ({
    emailAddress,
    subject,
    body,
    attachments,
    deviceAttachments,
    packages,
    sendEmailAction,
    inReplyToInternetMessageId
  }) => {
    const { emailSendingCurrentMatterData, matterHistorySettings, matterHistorySettingsDefaults } = get()

    get().getPayloadForEmailTemplate({
      emailAddress,
      subject,
      body
    })

    const { emailPayload } = get()

    const appendFormDataValueIfDifferentToDefault = (form, formKey, key) => {
      const currentValue = matterHistorySettings?.[key]
      const defaultValue = matterHistorySettingsDefaults?.[key]

      if (currentValue !== defaultValue) {
        form.append(formKey, currentValue)
      }
    }

    return new Promise((resolve, reject) => {
      debounce({
        id: 'sendEmail',
        delay: 600,
        callback: async () => {
          const form = new FormData()
          form.append('clientNo', emailSendingCurrentMatterData.clientNo)
          form.append('matterNo', Number(emailSendingCurrentMatterData.matterNo))
          form.append('sendEmailAction', sendEmailAction)
          form.append('mailMessageBase64Json', btoa(unescape(encodeURIComponent(JSON.stringify(emailPayload)))))

          if (inReplyToInternetMessageId) {
            form.append('inReplyToInternetMessageId', inReplyToInternetMessageId)
          }

          if (deviceAttachments?.length) {
            console.log('Appending device attachments:', deviceAttachments)
            deviceAttachments.forEach((a) => form.append('attachments', a.file))
          }

          if (deviceAttachments?.length) {
            deviceAttachments.forEach(() => form.append('isConvertWordToPdfs', 'false'))
          }

          if (attachments?.length) {
            attachments.forEach((a) => {
              form.append('matterHistoryContentIds', a.matterHistoryContentId)
            })
          }

          if (attachments?.length) {
            attachments.forEach((a) => {
              form.append(
                'matterHistoryContentIsConvertWordToPdfs',
                a.matterHistoryContentIsConvertWordToPdfs ? 'true' : 'false'
              )
            })
          }

          if (packages?.length) {
            packages.forEach((p) => {
              form.append('matterHistoryPackageIds', p.matterHistoryPackageId)
            })
          }

          appendFormDataValueIfDifferentToDefault(form, 'MatterHistoryEmailConfigurationId', 'matterHistoryEmailConfigurationId')
          appendFormDataValueIfDifferentToDefault(form, 'EmailConversationId', 'emailConversationId')

          appendFormDataValueIfDifferentToDefault(form, 'MatterHistoryDetailForReceivedEmail', 'matterHistoryDetailForReceivedEmail')
          appendFormDataValueIfDifferentToDefault(form, 'MatterHistoryDetailForSentEmail', 'matterHistoryDetailForSentEmail')

          appendFormDataValueIfDifferentToDefault(form, 'MatterHistoryFolderId', 'matterHistoryFolderId')
          appendFormDataValueIfDifferentToDefault(form, 'MatterHistoryTypeId', 'matterHistoryTypeId')

          appendFormDataValueIfDifferentToDefault(form, 'RetentionPolicyId', 'retentionPolicyId')
          appendFormDataValueIfDifferentToDefault(form, 'RetentionDateType', 'retentionDateType')

          appendFormDataValueIfDifferentToDefault(form, 'IsSaveEmailBody', 'isSaveEmailBody')
          appendFormDataValueIfDifferentToDefault(form, 'IsSaveEmailAttachments', 'isSaveEmailAttachments')
          appendFormDataValueIfDifferentToDefault(form, 'IsIgnoreInlineImages', 'isIgnoreInlineImages')
          appendFormDataValueIfDifferentToDefault(form, 'IgnoreInlineImagesBelowKilobytes', 'ignoreInlineImagesBelowKilobytes')

          /*
            // add this back when the backend supports it
            appendFormDataValueIfDifferentToDefault(form, 'DeleteDuplicateImagesAboveCount', 'deleteDuplicateImagesAboveCount')
          */

          try {
            const response = await ospreyAPI({
              headers: { 'Content-Type': 'multipart/form-data; charset=utf-8' },
              method: 'POST',
              service: 'emails',
              endpoint: `/Email/SendEmailWithAttachments`,
              data: form,
              timeout: 120000,
              suppressErrorToast: true,
              successToast: {
                type: 'success',
                title: `Email sent`,
                actions: [
                  {
                    label: 'Go to matter history',
                    onClick: () => {
                      navigateWithClientMatterNo({
                        route: `/case-management/matters/matter-history`,
                        appendClientMatterNo: true,
                        clientMatterNo: emailSendingCurrentMatterData.clientNo
                          ? `${emailSendingCurrentMatterData.clientNo}-${emailSendingCurrentMatterData.matterNo}`
                          : null
                      })
                    }
                  }
                ],
                text: 'Your email was sent successfully',
                clientMatterNo: emailSendingCurrentMatterData.clientNo
                  ? `${emailSendingCurrentMatterData.clientNo}-${emailSendingCurrentMatterData.matterNo}`
                  : null
              }
            })

            resolve(response)
          } catch (error) {
            console.error('Error sending email:', error)
            reject(error)
          }
        }
      })
    })
  },
  fetchEmailDetailsFromOsprey1: async ({ matterHistoryId }) => {
    if (matterHistoryId == null) return

    const isEmail = (s) => typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())

    const extractBracketEmail = (s) => {
      if (typeof s !== 'string') return null
      const m = s.match(/<\s*([^>]+@[^>]+)\s*>/)
      return m ? m[1].trim() : null
    }

    return new Promise((resolve) => {
      debounce({
        id: `fetchEmailDetailsFromOsprey1-${matterHistoryId}`,
        delay: 300,
        callback: async () => {
          try {
            const emailDetails = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `/MatterHistory/GetEmailMessageExtended?matterHistoryId=${matterHistoryId}`
            })

            const fromDisplayNameStr = emailDetails?.from?.displayName
            const fromDisplayIsEmail = isEmail(fromDisplayNameStr)
            const fromEmailBlank =
              emailDetails?.from?.emailAdresses === null ||
              emailDetails?.from?.emailAdresses?.trim() === '' ||
              emailDetails?.from?.emailAdresses === undefined
            const fromBracketEmail = extractBracketEmail(fromDisplayNameStr)

            const replaceBlankEmails = (recipientType) => {
              if (Array.isArray(emailDetails?.[recipientType])) {
                return emailDetails[recipientType].map((recipient) => {
                  const dn = recipient.displayName
                  const isDisplayNameEmail = isEmail(dn)
                  const bracketEmail = extractBracketEmail(dn)
                  const isAddressEmailBlank =
                    recipient.emailAdresses === null ||
                    recipient.emailAdresses?.trim() === '' ||
                    recipient.emailAdresses === undefined

                  return {
                    displayName: recipient.displayName,
                    emailAdresses: isAddressEmailBlank
                      ? isDisplayNameEmail
                        ? dn
                        : (bracketEmail ?? recipient.emailAdresses)
                      : recipient.emailAdresses
                  }
                })
              }
              return []
            }

            emailDetails.from = {
              displayName: fromDisplayNameStr,
              emailAdresses: fromEmailBlank
                ? fromDisplayIsEmail
                  ? fromDisplayNameStr
                  : (fromBracketEmail ?? emailDetails.from.emailAdresses)
                : emailDetails.from.emailAdresses
            }

            emailDetails.to = replaceBlankEmails('to')
            emailDetails.cc = replaceBlankEmails('cc')
            emailDetails.bcc = replaceBlankEmails('bcc')

            set({ emailDetailsFromOsprey1: emailDetails })
            resolve(emailDetails)
          } catch (error) {
            console.error('Error fetching email details from Osprey 1:', error)
            throw error
          }
        }
      })
    })
  },
  fetchAllMatterEmailConversationsByInternetMessageIds: async ({ internetMessageId }) => {
    return new Promise((resolve) => {
      debounce({
        id: `fetchAllMatterEmailConversationsByInternetMessageId-${internetMessageId}`,
        delay: 300,
        callback: async () => {
          try {
            const emailConversations = await ospreyAPI({
              method: 'GET',
              service: 'matters',
              endpoint: `/MatterHistory/GetAllMatterEmailConversationsByInternetMessageId`,
              data: {
                internetMessageId
              }
            })
            resolve(emailConversations)
          } catch (error) {
            console.error('Error fetching email conversations:', error)
            throw error
          }
        }
      })
    })
  },
  setAddAttachmentCallBack: (callback) => {
    if (typeof callback !== 'function') return
    set({ addAttachmentCallBack: callback })
  },
  setAddPackageCallBack: (callback) => {
    if (typeof callback !== 'function') return
    set({ addPackageCallBack: callback })
  },
  setEmailType: ({ emailType }) => {
    set({ emailType: emailType })
  },
  reset: () => {
    const cardData = [...get().cardData]
    const { addAttachmentCallBack, addPackageCallBack } = get()
    set({ ...initialState, cardData, addAttachmentCallBack, addPackageCallBack })
  }
}))
